/**
 * Types for the HexMap component.
 */

import type { AxialCoord } from '@/lib/hexMath';

/**
 * Terrain types matching the backend enum.
 */
export type TerrainType =
    | 'forest'
    | 'mountain'
    | 'plains'
    | 'swamp'
    | 'water'
    | 'city'
    | 'desert'
    | 'jungle'
    | 'tundra'
    | 'glacier'
    | 'lava'
    | 'beach';

/**
 * Terrain colors for rendering.
 */
export const TERRAIN_COLORS: Record<TerrainType, string> = {
    forest: '#228B22',
    mountain: '#8B4513',
    plains: '#90EE90',
    swamp: '#556B2F',
    water: '#4169E1',
    city: '#808080',
    desert: '#F4A460',
    jungle: '#006400',
    tundra: '#E0FFFF',
    glacier: '#B0E0E6',
    lava: '#FF4500',
    beach: '#FFE4B5',
};

/**
 * A hex on the map.
 */
export interface HexData extends AxialCoord {
    id: number;
    name: string;
    ownerName?: string | null; // Custom owner display name for enclave hexes (set by GMs)
    terrain: TerrainType; // fallback color
    domain?: 'deadlands' | 'drylands' | 'greenlands' | 'icelands' | 'sandlands';
    tileImage?: string; // filename within the domain directory
    defaultImage?: string; // default/fallback image path or filename
    isClaimable?: boolean;
    isOpenHex?: boolean;
    // Owner can be null for unclaimed hexes (configured by GM for player claiming)
    owner: {
        id: number;
        username: string;
    } | null;
}

/**
 * Viewport state for pan/zoom.
 */
export interface Viewport {
    offsetX: number;
    offsetY: number;
    zoom: number;
}

/**
 * Props for the HexMap component.
 */
export interface HexMapProps {
    hexes: HexData[];
    onHexClick?: (hex: HexData | null, coord: AxialCoord) => void;
    onHexHover?: (hex: HexData | null, coord: AxialCoord | null) => void;
    /** Set of hex IDs that were recently updated (for visual highlighting) */
    recentlyUpdatedIds?: Set<number>;
    /** Set of coordinate keys (e.g., "1,2") for unclaimed hexes that are claimable (adjacent to claimed) */
    claimableCoords?: Set<string>;
    /** Whether the current user can claim hexes (for tooltip messaging) */
    canClaimHexes?: boolean;
    /** Whether all unclaimed hexes are claimable (GMs can claim anywhere) */
    allUnclaimedClaimable?: boolean;
    /** Background theme for the map: the canvas adapts its clear/fill based on this */
    backgroundTheme?: 'current' | 'parchment' | 'starfield';
    className?: string;
}
